import React, { useState, useEffect, useRef } from 'react';
import {
    Badge,
    IconButton,
    Popover,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Typography,
    Box,
    Divider,
    Button,
    Tooltip,
    CircularProgress,
    ListItemSecondaryAction
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    Info as InfoIcon,
    CheckCircle as SuccessIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Delete as DeleteIcon,
    MoreVert as MoreIcon,
    DoneAll as DoneAllIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import NotificationMenu from './NotificationMenu';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
/**
 * Composant pour afficher et gérer les notifications
 */
const NotificationCenter = () => {
    const {
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        loadMoreNotifications
    } = useNotifications();

    const [anchorEl, setAnchorEl] = useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const navigate = useNavigate();
    const listRef = useRef(null);
    
    // Charger les notifications complètes lorsque le popover est ouvert
    useEffect(() => {
        if (anchorEl) {
            loadNotifications();
        }
    }, [anchorEl]);

    /**
     * Charger les notifications
     */
    const loadNotifications = async () => {
        if (loading || loadingMore) return;

        setLoadingMore(true);
        const result = await loadMoreNotifications({ page: 1, limit: 10 });
        setLoadingMore(false);

        if (result) {
            setPage(1);
            setHasMore(result.notifications.length < result.totalCount);
        }
    };

    /**
     * Charger plus de notifications (pagination)
     */
    const handleLoadMore = async () => {
        if (loading || loadingMore || !hasMore) return;

        setLoadingMore(true);
        const nextPage = page + 1;
        const result = await loadMoreNotifications({ page: nextPage, limit: 10 });
        setLoadingMore(false);

        if (result) {
            setPage(nextPage);
            setHasMore(result.currentPage * result.limit < result.totalCount);
        }
    };

    /**
     * Ouvrir le popover des notifications
     */
    const handleOpenNotifications = (event) => {
        setAnchorEl(event.currentTarget);
    };

    /**
     * Fermer le popover des notifications
     */
    const handleCloseNotifications = () => {
        setAnchorEl(null);
    };

    /**
     * Ouvrir le menu d'une notification
     */
    const handleOpenMenu = (event, notification) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setSelectedNotification(notification);
    };

    /**
     * Fermer le menu d'une notification
     */
    const handleCloseMenu = () => {
        setMenuAnchorEl(null);
        setSelectedNotification(null);
    };

    /**
     * Gérer le clic sur une notification
     */
    const handleNotificationClick = (notification) => {
        // Marquer la notification comme lue
        if (!notification.isRead) {
            markAsRead(notification.id);
        }

        // Naviguer vers le lien de la notification s'il existe
        if (notification.link) {
            navigate(notification.link);
            handleCloseNotifications();
        }
    };

    /**
     * Marquer toutes les notifications comme lues
     */
    const handleMarkAllAsRead = () => {
        markAllAsRead();
    };

    /**
     * Supprimer une notification
     */
    const handleDeleteNotification = (notificationId) => {
        deleteNotification(notificationId);
        handleCloseMenu();
    };

    /**
     * Obtenir l'icône en fonction du type de notification
     */
    const getNotificationIcon = (type) => {
        switch (type) {
            case 'success':
                return <SuccessIcon sx={{ color: 'success.main' }} />;
            case 'warning':
                return <WarningIcon sx={{ color: 'warning.main' }} />;
            case 'error':
                return <ErrorIcon sx={{ color: 'error.main' }} />;
            case 'info':
            default:
                return <InfoIcon sx={{ color: 'info.main' }} />;
        }
    };

    /**
     * Obtenir la couleur de fond en fonction du type de notification
     */
    const getNotificationColor = (type) => {
        switch (type) {
            case 'success':
                return 'success.light';
            case 'warning':
                return 'warning.light';
            case 'error':
                return 'error.light';
            case 'info':
            default:
                return 'info.light';
        }
    };

    /**
     * Formater la date relative
     */
    const formatRelativeTime = (date) => {
        try {
            return formatDistanceToNow(new Date(date), { addSuffix: true, locale: fr });
        } catch (error) {
            return 'Date inconnue';
        }
    };

    const open = Boolean(anchorEl);
    const menuOpen = Boolean(menuAnchorEl);
    const { t } = useTranslation();
    return (
        <>
        <Tooltip title={t('notifications.title')}>
            <IconButton
                color="inherit"
                onClick={handleOpenNotifications}
                aria-label={t('notifications.ariaLabel.unreadCount', { count: unreadCount })}
            >
                <Badge badgeContent={unreadCount} color="error">
                    <NotificationsIcon />
                </Badge>
            </IconButton>
        </Tooltip>

        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleCloseNotifications}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            PaperProps={{
                sx: {
                    width: 360,
                    maxHeight: 500,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <Box sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: 1,
                borderColor: 'divider'
            }}>
                <Typography variant="h6">{t('notifications.title')}</Typography>
                {unreadCount > 0 && (
                    <Tooltip title={t('notifications.markAllAsRead')}>
                        <IconButton
                            size="small"
                            onClick={handleMarkAllAsRead}
                            aria-label={t('notifications.ariaLabel.markAllAsRead')}
                        >
                            <DoneAllIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Box sx={{ overflow: 'auto', flexGrow: 1 }} ref={listRef}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress size={40} />
                    </Box>
                ) : notifications.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="textSecondary">
                            {t('notifications.empty.description')}
                        </Typography>
                    </Box>
                ) : (
                    <List disablePadding>
                        {notifications.map((notification) => (
                            <React.Fragment key={notification.id}>
                                <ListItem
                                    button
                                    alignItems="flex-start"
                                    onClick={() => handleNotificationClick(notification)}
                                    sx={{
                                        bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                                        transition: 'background-color 0.2s',
                                        '&:hover': {
                                            bgcolor: 'action.selected'
                                        }
                                    }}
                                >
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: getNotificationColor(notification.type) }}>
                                            {getNotificationIcon(notification.type)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={notification.isRead ? 'normal' : 'bold'}
                                            >
                                                {notification.title}
                                            </Typography>
                                        }
                                        secondary={
                                            <>
                                                <Typography
                                                    variant="body2"
                                                    color="textPrimary"
                                                    sx={{
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        mb: 0.5
                                                    }}
                                                >
                                                    {notification.message}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                >
                                                    {formatRelativeTime(notification.createdAt)}
                                                </Typography>
                                            </>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            edge="end"
                                            size="small"
                                            onClick={(e) => handleOpenMenu(e, notification)}
                                            aria-label={t('notifications.ariaLabel.options')}
                                        >
                                            <MoreIcon fontSize="small" />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                                <Divider component="li" />
                            </React.Fragment>
                        ))}
                    </List>
                )}

                {hasMore && (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Button
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                            startIcon={loadingMore ? <CircularProgress size={20} /> : null}
                        >
                            {loadingMore ? t('common.loading') : t('notifications.loadMore')}
                        </Button>
                    </Box>
                )}
            </Box>

            <Box sx={{
                p: 1.5,
                borderTop: 1,
                borderColor: 'divider',
                textAlign: 'center'
            }}>
                <Button
                    variant="text"
                    onClick={() => {
                        navigate('/notifications');
                        handleCloseNotifications();
                    }}
                >
                    {t('notifications.viewAll')}
                </Button>
            </Box>
        </Popover>

        <NotificationMenu
            open={menuOpen}
            anchorEl={menuAnchorEl}
            onClose={handleCloseMenu}
            notification={selectedNotification}
            onMarkAsRead={() => {
                if (selectedNotification) {
                    markAsRead(selectedNotification.id);
                    handleCloseMenu();
                }
            }}
            onDelete={() => {
                if (selectedNotification) {
                    handleDeleteNotification(selectedNotification.id);
                }
            }}
        />
    </>
    );
};

export default NotificationCenter;